// Mid-session alerts for new dashboard announcements. The dashboard-posts store calls
// notifyNewAnnouncements with the posts that appeared since its previous fetch (it owns the
// known-ids diff and seeding); this module decides HOW to inform:
//   - viewing the Dashboard (window visible + active page "Dashboard") → nothing, the feed
//     updates live with its NEW badges;
//   - window visible on another page → a sonner toast with an "Open Dashboard" action;
//   - window hidden/minimized (or a background browser tab) → an OS notification, click focuses
//     + opens Dashboard. Web mode shows it only once the user enabled notifications for this
//     browser (system-notifications never prompts outside a click).
// The red nav pip is independent of all of this — only a dashboard visit clears it.
// Own-authored posts and DND sessions are always silent.

import { toast } from 'sonner';
import { getAuthToken, getAuthUser } from '@/lib/api/token-store';
import { requestOpenDashboard } from '@/lib/notifications/notification-navigation';
import { ensureNotificationPermission, showSystemNotification } from '@/lib/notifications/system-notifications';
import { getSelfAvailability } from '@/lib/realtime/presence-service';
import { getUserIdsFromJwt } from '@/lib/realtime/jwt-user-ids';
import { getWorkspacesSnapshot } from '@/lib/stores/workspace-store';

const PREVIEW_MAX_CHARS = 140;

function isOwnPost(post) {
  const authorId = String(post?.authorId || '').trim();
  if (!authorId) {
    return false;
  }
  const currentUserId = String(getAuthUser()?.id || '').trim();
  if (currentUserId && authorId === currentUserId) {
    return true;
  }
  return getUserIdsFromJwt(getAuthToken()).includes(authorId);
}

function isViewingDashboard() {
  if (typeof document === 'undefined' || document.visibilityState !== 'visible') {
    return false;
  }
  const snapshot = getWorkspacesSnapshot();
  const active = snapshot.workspaces[snapshot.activeUserId];
  return Boolean(snapshot.activeUserId) && active?.activePage === 'Dashboard';
}

// Markdown bodies read badly in a one-line preview: drop the common markers and collapse
// whitespace. Not a renderer — just enough that "**Closed** on [Friday](…)" reads plainly.
function plainPreview(markdown) {
  const text = String(markdown || '')
    .replace(/\[([^\]]*)\]\([^)]*\)/g, '$1')
    .replace(/[*_`~#>]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
  if (text.length <= PREVIEW_MAX_CHARS) {
    return text;
  }
  return `${text.slice(0, PREVIEW_MAX_CHARS)}…`;
}

function showToast(posts) {
  const action = { label: 'Open Dashboard', onClick: () => requestOpenDashboard() };
  if (posts.length === 1) {
    toast.info(`📣 ${posts[0].title || 'New announcement'}`, {
      description: plainPreview(posts[0].body),
      action,
    });
    return;
  }
  toast.info(`📣 ${posts.length} new announcements`, {
    description: posts
      .slice(0, 3)
      .map((post) => String(post.title || '').trim())
      .filter(Boolean)
      .join(' · '),
    action,
  });
}

async function showOsNotification(posts) {
  const ok = await ensureNotificationPermission();
  if (!ok) {
    return;
  }
  // The 📣 prefix is the cross-platform differentiator from chat notifications — macOS
  // ignores per-notification icons (chat-message-notifications convention).
  const title =
    posts.length === 1
      ? `📣 ${posts[0].title || 'New announcement'}`
      : `📣 ${posts.length} new announcements`;
  const body =
    posts.length === 1
      ? plainPreview(posts[0].body)
      : posts
          .slice(0, 3)
          .map((post) => String(post.title || '').trim())
          .filter(Boolean)
          .join(' · ');
  showSystemNotification({
    title,
    body,
    // Web mode: a duplicated tab diffs the same batch on its own — one tag per batch collapses it.
    tag: `announcement:${posts
      .map((post) => String(post.id || ''))
      .sort()
      .join(',')}`,
    onClick: requestOpenDashboard,
  });
}

/**
 * Inform the signed-in user about announcements that just arrived. `posts` must already be
 * the genuinely-new subset (the store diffs against its known ids and never passes the
 * login-time backlog).
 */
export function notifyNewAnnouncements(posts) {
  const fresh = (Array.isArray(posts) ? posts : []).filter(
    (post) => post && typeof post === 'object' && !isOwnPost(post),
  );
  if (fresh.length === 0) {
    return;
  }
  // Do-not-disturb silences announcements like every other notification; the red nav pip
  // still updates through the store.
  if (getSelfAvailability() === 'dnd') {
    return;
  }
  if (isViewingDashboard()) {
    return;
  }
  const windowVisible = typeof document !== 'undefined' && document.visibilityState === 'visible';
  if (windowVisible) {
    showToast(fresh);
    return;
  }
  void showOsNotification(fresh);
}
